// src/FeedbackForm.js
//
// Two dropdowns (Beneficiary required, Activity optional per the real
// model: activity has null=True, blank=True).

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function FeedbackForm({ token, feedback, onSaved, onCancel }) {
  const [beneficiaryOptions, setBeneficiaryOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);

  const [beneficiary, setBeneficiary] = useState(feedback?.beneficiary || '');
  const [activity, setActivity] = useState(feedback?.activity || '');
  const [message, setMessage] = useState(feedback?.message || '');
  const [status, setStatus] = useState(feedback?.status || 'new');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Token ${token}` };
    fetch(`${API_BASE_URL}/api/beneficiaries/`, { headers })
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setBeneficiaryOptions(data); });

    fetch(`${API_BASE_URL}/api/activities/`, { headers })
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setActivityOptions(data); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      beneficiary,
      activity: activity || null,
      message,
      status,
    };

    const url = feedback
      ? `${API_BASE_URL}/api/feedback/${feedback.id}/`
      : `${API_BASE_URL}/api/feedback/`;
    const method = feedback ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{feedback ? 'Edit Feedback' : 'New Feedback'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary</label>
        <select value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="" disabled>Select a beneficiary...</option>
          {beneficiaryOptions.map((b) => (
            <option key={b.id} value={b.id}>{b.full_name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Activity (optional)</label>
        <select value={activity} onChange={(e) => setActivity(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">None</option>
          {activityOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows="4" required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="resolved">Resolved</option>
        </select>

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default FeedbackForm;
